import { startOfDay } from "date-fns";
import type { TimeSlotDefinition } from "../../utils/publicationDistribution";

export type InsertionCard = { id: string; time: number; order: number };
export type InsertionTarget = { time: number; index: number };
export type CalendarPlacementTarget = {
  slotKey: string;
  time: number;
  top: number;
  original?: boolean;
};

/** One useful half-hour per chronological gap, plus the preferred time.
 * Cards are already sorted by time/source order; the lifted card is omitted. */
export function getInsertionTargets({ day, slot, cards, now, sourceOrder = Infinity }: {
  day: Date;
  slot: TimeSlotDefinition;
  cards: readonly InsertionCard[];
  now: number;
  sourceOrder?: number;
}): InsertionTarget[] {
  if (slot.isDisabled) return [];
  const midnight = startOfDay(day);
  // Construct local wall-clock times rather than dividing epoch milliseconds:
  // daylight-saving transitions must not shift the displayed hours.
  const at = (minute: number) => {
    const date = new Date(midnight);
    date.setHours(0, minute, 0, 0);
    return date.getTime();
  };
  const preferred = at((slot.preferredHour ?? slot.startHour) * 60);
  const times: number[] = [];
  for (let minute = slot.startHour * 60; minute < (slot.endHour + 1) * 60; minute += 30) {
    const time = at(minute);
    if (time > now) times.push(time);
  }
  const candidates: number[] = [];
  for (let index = 0; index <= cards.length; index++) {
    const lower = cards[index - 1]?.time ?? -Infinity;
    const upper = cards[index]?.time ?? Infinity;
    const available = times.filter(time => time > lower && time < upper);
    available.sort((a, b) => Math.abs(a - preferred) - Math.abs(b - preferred) || a - b);
    if (available.length) candidates.push(available[0]);
  }
  if (times.includes(preferred)) candidates.push(preferred);
  const byPosition = new Map<number, InsertionTarget>();
  for (const time of candidates) {
    const index = cards.filter(card => card.time < time || (card.time === time && card.order < sourceOrder)).length;
    const existing = byPosition.get(index);
    if (!existing || time === preferred) byPosition.set(index, { time, index });
  }
  return [...byPosition.values()].sort((a, b) => a.index - b.index);
}

export function positionInsertionTargets(targets: readonly InsertionTarget[], cards: readonly { top: number; bottom: number }[], start: number, end: number) {
  return targets.map(target => {
    const previous = cards[target.index - 1];
    const next = cards[target.index];
    const top = previous && next ? (previous.bottom + next.top) / 2
      : previous ? previous.bottom + 4 : next ? next.top - 4 : start + 8;
    return { ...target, top: Math.max(start, Math.min(end, top)) };
  });
}

export function nearestInsertionTarget<T extends { top: number }>(targets: readonly T[], pointerY: number): T | null {
  return targets.reduce<T | null>((nearest, target) =>
    !nearest || Math.abs(target.top - pointerY) < Math.abs(nearest.top - pointerY) ? target : nearest, null);
}

/** Extend bands across their day's padding, respecting occupied bands'
 * unequal heights. Measure distance to their edges, not their centres. */
export function nearestCalendarBand<T extends { top: number; bottom: number }>(bands: readonly T[], pointerY: number): T | null {
  const distance = (band: T) => Math.max(band.top - pointerY, pointerY - band.bottom, 0);
  return bands.reduce<T | null>((nearest, band) =>
    !nearest || distance(band) < distance(nearest) ? band : nearest, null);
}
